import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { nexiAPI } from '../config/api';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const NexiResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { clearCart } = useCart();
  const clearedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending'); // 'pending' | 'success' | 'failed'
  const [error, setError] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const orderIdParam = searchParams.get('orderId');
  const outcomeParam = searchParams.get('esito') || searchParams.get('outcome');

  useEffect(() => {
    let isMounted = true;

    const verifyPayment = async () => {
      if (!orderIdParam) {
        setError(t('nexi.result.invalid_order'));
        setStatus('failed');
        setLoading(false);
        return;
      }

      const numericOrderId = parseInt(orderIdParam, 10);
      if (Number.isNaN(numericOrderId) || numericOrderId <= 0) {
        setError(t('nexi.result.invalid_order'));
        setStatus('failed');
        setLoading(false);
        return;
      }

      try {
        const result = await nexiAPI.checkStatus(numericOrderId);
        if (!isMounted) return;

        if (result.paid) {
          if (!clearedRef.current) {
            clearCart();
            clearedRef.current = true;
          }
          setStatus('success');
        } else {
          setStatus(outcomeParam && outcomeParam.toUpperCase() === 'OK' ? 'pending' : 'failed');
          if (!result.paid) {
            setError(t('nexi.result.not_confirmed'));
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || t('nexi.result.error_generic'));
        setStatus('failed');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    verifyPayment();

    return () => {
      isMounted = false;
    };
  }, [orderIdParam, outcomeParam, clearCart, t]);

  const handleBackHome = () => {
    navigate('/');
  };

  const handleBackCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-success">
          {loading && (
            <>
              <div className="success-icon">⌛</div>
              <h1>{t('nexi.result.verifying')}</h1>
              <p>{t('nexi.result.wait_message')}</p>
            </>
          )}

          {!loading && status === 'success' && (
            <>
              <div className="success-icon">✓</div>
              <h1>{t('nexi.result.success_title')}</h1>
              <p>{t('nexi.result.success_message')}</p>
              <button className="btn btn-primary" onClick={handleBackHome}>
                {t('nexi.result.back_home')}
              </button>
            </>
          )}

          {!loading && status !== 'success' && (
            <>
              <div className="success-icon error">!</div>
              <h1>{t('nexi.result.failed_title')}</h1>
              <p>{error || t('nexi.result.error_generic')}</p>
              <div className="result-actions">
                <button className="btn btn-outline" onClick={handleBackCheckout}>
                  {t('nexi.result.back_checkout')}
                </button>
                <button className="btn btn-primary" onClick={handleBackHome}>
                  {t('nexi.result.back_home')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NexiResult;

